import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { Camera, Check, Loader2, RefreshCw, SwitchCamera, X } from "lucide-react";
import { inkViews, markPlace, needsHelper, type InkView, type MarkRegion, type MarkSide } from "@/shared/face-qc/marks.mjs";
import { buzz, cameraFailureOf, cameraSupported, chime, stopStream, unlockSound, type CameraFailure } from "@/lib/face-scan/platform";
import { toGray } from "@/lib/face-scan/pixels";
import { InkJudge, LOCKJ, measureInk, onMark, sideChecked, within, type InkLock, type InkProblem, type InkSample, type InkVerdict } from "@/lib/face-scan/ink";
import { finderFor, type Box01, type InkFind, type InkSignature, type MarkKind } from "@/lib/face-scan/ink-find";
import { FACE_FIX, INK_FACE_PLACES, anchorBox, belowChin, faceFixOf, freshFix, sideOf, type FaceFix } from "@/lib/face-scan/ink-face";
import type { Vision, VisionHub } from "@/lib/face-scan/vision";
import { createScanTelemetry, type ScanTelemetry, type TelemetryPayload } from "@/lib/face-scan/telemetry";
import type { FaceLockMark } from "@/lib/facelock";
import { SCAN_UI } from "../scan/ui";
import InkOverlay from "./InkOverlay";

// FaceLock Ink Scan, full screen (an avatar enhancement; owner direction
// 2026-10-06: "perfectly depict a user's tattoos"). For each mark the person
// named: on the body, one wider picture of where it sits, then two close-ups
// that fill the frame (straight on, then a little to the side).
//
// It LOCATES the mark and LOCKS ON to it before it takes anything (owner,
// 2026-10-07: on his phone it rushed and kept pictures with no tattoo in
// them). Several times a second a small colour copy of the frame is looked at:
// the finder (lib/face-scan/ink-find.ts) says where the mark is, the judge
// (lib/face-scan/ink.ts) says when it is locked on, centred, lit, free of
// shine, held still and sharp, and the picture is taken then, by itself, never
// in the first seconds of a view (time to get in place and focus). Brackets on
// the preview show what it locked on to. On the face, neck and head the face
// model the Scan already ships (ink-face.ts) masks the eyes, brows and lips,
// points at the named place and refuses the wrong side (owner, 2026-10-07: he
// has many face tattoos); when it is slow, off or fails, the scan carries on
// with pixels alone and says nothing.
//
// Nothing is taken by itself without a lock on the mark (a scar too pale to
// find: skin in the box). The scan still always finishes: "Take it now" always
// works (owner, 2026-10-07: it fired before he was in place), a view that
// cannot lock says so from 20 s and points at it, and a late 3-2-1 countdown
// runs only while a lock is held. A picture taken by hand when the judge did
// not see the named mark in it (no lock, the wrong side, off-centre, another
// mark) is flagged in the review. The whole camera frame is kept at the camera's own
// size (the full photo where the browser can take one), never mirrored: the
// person's left stays their left. A back mark is taken with the back camera,
// by someone else or with the phone propped up.

export interface InkMark extends FaceLockMark {
  /** The id the server knows it by (m1, m2...: its order in the request). */
  id: string;
}

/** How a picture was taken: by itself, by the late countdown, or by hand ("Take it now"). */
export type InkTakenBy = "auto" | "countdown" | "manual";

export interface InkShot {
  mark: string;
  view: InkView;
  blob: Blob;
  width: number;
  height: number;
  /**
   * The judge saw the mark it locked on to in the frame that was taken (ink.ts onMark). False: by hand with no lock,
   * or on the wrong side, off-centre, another mark, a miss: the review asks the person to check it.
   */
  locked: boolean;
  by: InkTakenBy;
  /**
   * Where the judge saw the tattoo in the frame that was taken, [x, y, w, h] fractions of the (unmirrored) picture;
   * null without a lock. Training learns the picture there and barely anywhere else (owner, 2026-10-11: "THE INK
   * SCAN IS SOLELY ABOUT TATTOOS... NOTHING ELSE... BUT THE TATTOOS AND THEIR POSITION ON THE CHARACTER").
   */
  box: [number, number, number, number] | null;
}

type Item = { key: string; mark: InkMark; view: InkView };
type Step = { name: "intro" } | { name: "starting" } | { name: "stage" } | { name: "review" } | { name: "error"; reason: CameraFailure };
/** A kept picture, with the small copy the review shows (never the full photo: 12 marks are up to 36 pictures of up to 12 MP). */
type Kept = InkShot & { preview: string | null };
/**
 * What a view keeps for the next one: close_b is checked against close_a, and carries its lock when close_a was taken
 * on one. `sig` and `box` only when the judge saw the mark in the picture (a stray find held "That looks like a
 * different one" over the right tattoo for 14 s). `session` and `at`: the carry is only for the close_b that follows
 * close_a's picture in the same camera session (a retake from the review, minutes later, started locked on an old box).
 */
type ViewRef = { thumb: number[] | null; sig: InkSignature | null; box: Box01 | null; lock: InkLock; by: InkTakenBy; session: number; at: number };
/** What the camera view shows of the judge's verdict (set only when it changes). */
type LookState = { lock: InkLock; box: Box01 | null; tol: number; problem: InkProblem | null; stuck: boolean; skinOnly: boolean };

/** The camera an Ink Scan asks for: as sharp as the camera gives at a usable pace. */
const INK_CAMERA = (facing: "user" | "environment"): MediaTrackConstraints => ({
  facingMode: facing,
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  frameRate: { ideal: 30 },
});
/** How often the close-up is looked at, and the size of the copy looked at. */
const LOOK_MS = 125;
const LOOK_SIDE = 320;
/**
 * A slow phone keeps its preview smooth: the finder's look (the copy, the
 * checks and the finder, a rolling mean of LOOK_MEAN looks) over the first
 * figure runs on every 2nd look, over the second on every 3rd; the looks in
 * between reuse its last answer and do not move the lock.
 */
const LOOK_MEAN = 8;
const LOOK_SLOW_MS = [20, 40] as const;
/** The telemetry's report of what a view reads: this often, at most this many per view (a minute). */
const INK_REPORT_MS = 2000;
const INK_REPORTS_MAX = 30;
const r1 = (x: number) => (Number.isFinite(x) ? Math.round(x * 10) / 10 : null);
const r3 = (x: number) => (Number.isFinite(x) ? Math.round(x * 1000) / 1000 : null);
/**
 * The face model on face, neck and head marks: first at FACE_FIRST_MS into a
 * view, then every max(FACE_EVERY_MS, 4 x its own time), never inside the look
 * (it takes about 0.15 s on a phone). Given FACE_TIMEOUT_MS to start; any
 * failure turns it off for the rest of the scan. It never rests while the view
 * is up, and a fix lasts until the next one is due (FACE_LAST x the gap, at
 * least FACE_FIX.maxAgeMs): resting it once two fixes agreed let the fix go
 * stale 1.2 s later, before the picture was judged, and the unmasked brow beat
 * the tattoo under it, or a side read on one cheek was kept for the other
 * (2026-10-07 review). Only where it helps: a place whose side it checks or
 * whose spot it points at (not the top or the back of the head, nor the back
 * of the neck: from behind there is no face to see).
 */
const FACE_FIRST_MS = 300;
const FACE_EVERY_MS = 800;
const FACE_TIMEOUT_MS = 8000;
const FACE_LAST = 1.5;
/** close_b starts locked on close_a's mark only this soon after close_a's picture, in the same camera session. */
const CARRY_MS = 2000;
/** Focus (and exposure, on Android) on the mark: once per lock, again when it moved this much, at most once a second. */
const FOCUS_MOVE = 0.12;
const FOCUS_EVERY_MS = 1000;
/** The full photo is checked again: the mark found within this of the centre, else the judged video frame is kept (a 4:3 photo shows more). */
const RECHECK_TOL = 0.25;
/** The review grid's copies: this long side, px. */
const REVIEW_SIDE = 480;
/** The full photo is waited for at most this long (else the video frame is kept). */
const STILL_TIMEOUT_MS = 2500;

type StillCapture = { takePhoto: () => Promise<Blob> };
type StillCaptureCtor = new (track: MediaStreamTrack) => StillCapture;

/** The longest side a full photo is kept at (a 12 MP still is 4000 x 3000; this bounds the memory of one picture). */
const STILL_MAX_SIDE = 4096;

/** A look-size colour copy of an image, area-averaged (the finder needs that downscale, not a plain bilinear one). */
type LookCopy = { rgba: Uint8ClampedArray; w: number; h: number };

/** The camera hint: the box the person is asked to put the mark in, centre plus or minus tol. */
const guideOf = (tol: number): Box01 => ({ x: 0.5 - tol, y: 0.5 - tol, w: 2 * tol, h: 2 * tol });

function placeOf(place: string): { region: MarkRegion; side: MarkSide } {
  const p = markPlace(place);
  return { region: p?.region ?? "body", side: p?.side ?? "center" };
}

/**
 * Which way the second close-up asks to turn (owner, 2026-10-08: "turn to the side... what side?"). A mark on the head,
 * face or neck: the head turns AWAY from the mark's side, so the camera sees that side (a mark on the left: turn to
 * your right; one in the middle: to your left). On the body: that part turns a quarter turn, or the camera moves.
 */
function sideWayOf(place: string): "turnRight" | "turnLeft" | "body" {
  const { region, side } = placeOf(place);
  if (region === "body" || side === "back") return "body";
  return side === "left" ? "turnRight" : "turnLeft";
}

/**
 * A full photo as a JPEG (some browsers hand a PNG), upright (createImageBitmap applies its orientation), within
 * STILL_MAX_SIDE; with `lookSide`, also a copy that size for the finder.
 */
async function asJpeg(blob: Blob, lookSide = 0): Promise<{ blob: Blob; width: number; height: number; look: LookCopy | null } | null> {
  try {
    const bmp = await createImageBitmap(blob);
    const k = Math.min(1, STILL_MAX_SIDE / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * k);
    const h = Math.round(bmp.height * k);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) { bmp.close(); return null; }
    ctx.drawImage(bmp, 0, 0, w, h);
    let look: LookCopy | null = null;
    if (lookSide) {
      const s = lookSide / Math.max(bmp.width, bmp.height);
      const lw = Math.max(16, Math.round(bmp.width * s));
      const lh = Math.max(16, Math.round(bmp.height * s));
      const small = document.createElement("canvas");
      small.width = lw;
      small.height = lh;
      const sc = small.getContext("2d", { willReadFrequently: true });
      if (sc) {
        sc.imageSmoothingEnabled = true;
        sc.imageSmoothingQuality = "high";
        sc.drawImage(bmp, 0, 0, lw, lh);
        look = { rgba: sc.getImageData(0, 0, lw, lh).data, w: lw, h: lh };
      }
    }
    bmp.close();
    const jpeg = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    return jpeg ? { blob: jpeg, width: w, height: h, look } : null;
  } catch {
    return null;
  }
}

/** What the kept full photo must still show: the mark, near the centre (automatic pictures of tattoos, moles and birthmarks). */
type Recheck = { kind: MarkKind; face: boolean; stage: 0 | 1 | 2 };

/**
 * The whole frame at the camera's size; the browser's full photo when it can give a bigger one in time. The full
 * photo can come up to STILL_TIMEOUT_MS after the frame that was judged: with `recheck`, it is kept only when the
 * mark is still in it, near the centre (else the judged video frame is).
 */
async function takePicture(video: HTMLVideoElement, stream: MediaStream | null, recheck: Recheck | null): Promise<{ blob: Blob; width: number; height: number } | null> {
  const W = video.videoWidth;
  const H = video.videoHeight;
  if (!W || !H) return null;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // Never mirrored: only the preview on screen is.
  ctx.drawImage(video, 0, 0, W, H);
  const frame = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  const Ctor = (globalThis as unknown as { ImageCapture?: StillCaptureCtor }).ImageCapture;
  const track = stream?.getVideoTracks()[0];
  if (Ctor && track) {
    try {
      const photo = await Promise.race([
        new Ctor(track).takePhoto(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), STILL_TIMEOUT_MS)),
      ]);
      if (photo) {
        const still = await asJpeg(photo, recheck ? LOOK_SIDE : 0);
        if (still && still.width * still.height > W * H) {
          const look = still.look;
          const mark = recheck && look ? finderFor(look.w, look.h)(look.rgba, recheck.kind, { face: recheck.face, stage: recheck.stage }) : null;
          if (!recheck || (mark && mark.found && within(mark, RECHECK_TOL))) return { blob: still.blob, width: still.width, height: still.height };
        }
      }
    } catch { /* the video frame it is */ }
  }
  return frame ? { blob: frame, width: W, height: H } : null;
}

/** The review grid's copy of a picture (REVIEW_SIDE), as an object URL; null when the browser cannot make one. */
async function reviewCopy(blob: Blob): Promise<string | null> {
  try {
    const bmp = await createImageBitmap(blob);
    const k = Math.min(1, REVIEW_SIDE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * k));
    canvas.height = Math.max(1, Math.round(bmp.height * k));
    const ctx = canvas.getContext("2d");
    if (!ctx) { bmp.close(); return null; }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    const small = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    return small ? URL.createObjectURL(small) : null;
  } catch {
    return null;
  }
}

/** Ask the camera to focus (and, on Android, meter) on the mark, where the browser lets the page; never fails the scan. */
function focusOn(stream: MediaStream | null, c: { x: number; y: number }) {
  try {
    const supported = navigator.mediaDevices?.getSupportedConstraints?.() as Record<string, boolean> | undefined;
    const track = stream?.getVideoTracks()[0];
    if (!supported?.pointsOfInterest || !track) return;
    const caps = (track.getCapabilities?.() ?? {}) as { focusMode?: string[] };
    const focusMode = caps.focusMode?.includes("continuous") ? "continuous" : "single-shot";
    // The point is in the unmirrored frame, as the finder's centre is: no mapping.
    void track.applyConstraints({ advanced: [{ pointsOfInterest: [{ x: c.x, y: c.y }], focusMode } as MediaTrackConstraintSet] }).catch(() => undefined);
  } catch { /* no focus help: fine */ }
}

/** The look state's identity: the screen changes only when this does. */
const lookKey = (s: LookState) =>
  `${s.lock}|${s.problem ?? ""}|${s.stuck ? 1 : 0}|${s.skinOnly ? 1 : 0}|${s.tol}|${s.box ? boxAttr(s.box) : ""}`;
/** A box as the test hook writes it: x,y,w,h, unmirrored fractions, 3 decimals. */
const boxAttr = (b: Box01) => `${b.x.toFixed(3)},${b.y.toFixed(3)},${b.w.toFixed(3)},${b.h.toFixed(3)}`;
const lookStart = (region: MarkRegion): LookState => ({
  lock: "searching", box: null, tol: LOCKJ.centre[region === "body" ? "body" : "face"][0], problem: null, stuck: false, skinOnly: false,
});

export default function InkScan({ marks, onDone, onClose, faceAssist = true, telemetrySend }: {
  marks: InkMark[];
  onDone: (shots: InkShot[]) => void;
  onClose: () => void;
  /** Face, neck and head marks get the face model's help (masks, the named place, the side). Off: pixels alone. */
  faceAssist?: boolean;
  /** Where the telemetry goes (default: the signed-in route; tests pass their own). */
  telemetrySend?: (payload: TelemetryPayload) => boolean;
}) {
  const { t } = useTranslation();
  // What the Ink Scan saw on a real device, as numbers (owner, 2026-10-08: on his computer it never locked on, and
  // nothing said why): each view's start, every INK_REPORT_MS what the finder and the judge read, and every picture
  // taken. Never a picture: lib/face-scan/telemetry.ts and the server keep flat numbers and short words only.
  const tel = React.useMemo<ScanTelemetry>(() => createScanTelemetry(telemetrySend ? { send: telemetrySend } : {}), []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    tel.track("ink_open", { data: { marks: marks.length, faceAssist } });
    return () => { tel.track("ink_close"); tel.dispose(); };
  }, [tel]); // eslint-disable-line react-hooks/exhaustive-deps
  // The views, kept while the marks are the same ones: a parent that re-renders hands a new array each time (the crew
  // list re-reads the server every 15 s while any avatar is busy), and each new array restarted the view's judge: the
  // lock dropped, and 20 s ("stuck") and 30 s (the countdown) were never reached (2026-10-07 review).
  const marksKey = marks.map((m) => `${m.id}|${m.place}|${m.kind}`).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo<Item[]>(() => marks.flatMap((m) => inkViews(m.place).map((view) => ({ key: `${m.id}:${view}`, mark: m, view }))), [marksKey]);
  const [step, setStep] = React.useState<Step>({ name: "intro" });
  const [facing, setFacing] = React.useState<"user" | "environment">(() => (marks.some((m) => needsHelper(m.place)) ? "environment" : "user"));
  const facingRef = React.useRef(facing);
  facingRef.current = facing;
  const [shots, setShots] = React.useState<Record<string, Kept>>({});
  const [skipped, setSkipped] = React.useState<Record<string, boolean>>({});
  const [idx, setIdx] = React.useState(0);
  const [look, setLook] = React.useState<LookState>(() => lookStart("body"));
  const [frame, setFrame] = React.useState<{ w: number; h: number } | null>(null);
  const [countdown, setCountdown] = React.useState<number | null>(null);
  const [flash, setFlash] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const takingRef = React.useRef(false);
  const aliveRef = React.useRef(true);
  const shotsRef = React.useRef(shots);
  shotsRef.current = shots;
  /** Per view: what close_b checks against (thumbnail, mark signature) and whether close_a's lock carries. */
  const refsRef = React.useRef<Record<string, ViewRef>>({});
  /** The view's judge, its last look and verdict ("Take it now" takes with these), the last mark found (the focus point). */
  const judgeRef = React.useRef<InkJudge | null>(null);
  const lastSampleRef = React.useRef<InkSample | null>(null);
  const verdictRef = React.useRef<InkVerdict | null>(null);
  const lastFoundRef = React.useRef<InkFind | null>(null);
  const lookSizeRef = React.useRef<{ w: number; h: number } | null>(null);
  const frameRef = React.useRef<{ w: number; h: number } | null>(null);
  /** The camera view, for its look-time hook (data-look-ms, written without a render). */
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  /** The face help: the latest fix, the models (loaded on the first face mark), off for good after a failure. */
  const fixRef = React.useRef<FaceFix | null>(null);
  const hubRef = React.useRef<VisionHub | null>(null);
  const visionRef = React.useRef<Promise<Vision | null> | null>(null);
  const faceGenRef = React.useRef(0);
  const faceOffRef = React.useRef(!faceAssist);
  /** How long the latest fix is used: until the next one is due (a slow phone runs the face model less often). */
  const faceAgeRef = React.useRef<number>(FACE_FIX.maxAgeMs);
  /** The camera session: a new one on every open (a retake, a camera switch): close_a's lock carries only within one. */
  const sessionRef = React.useRef(0);

  const item = items[idx] ?? null;

  const stopCamera = React.useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    setStream(null);
  }, []);

  /** Close the face model (the scan is closed, done, or in its review); a load still on its way is dropped. */
  const closeFace = React.useCallback(() => {
    faceGenRef.current += 1;
    const hub = hubRef.current;
    hubRef.current = null;
    visionRef.current = null;
    fixRef.current = null;
    try { hub?.close(); } catch { /* already gone */ }
  }, []);

  /** The face model, loaded once (the same one the Scan ships, so nothing new is downloaded); null when it cannot help. */
  const faceVision = React.useCallback((): Promise<Vision | null> => {
    if (faceOffRef.current) return Promise.resolve(null);
    if (!visionRef.current) {
      const gen = faceGenRef.current;
      visionRef.current = import("@/lib/face-scan/vision")
        .then(({ createVisionHub }) => {
          if (gen !== faceGenRef.current) return null;
          const hub = createVisionHub("VIDEO");
          hubRef.current = hub;
          return hub.use({ face: true, pose: false }, { timeoutMs: FACE_TIMEOUT_MS });
        })
        .catch(() => {
          // Any failure (the download, the model, the deadline): pixels alone for the rest of the scan, without a word.
          if (gen === faceGenRef.current) {
            faceOffRef.current = true;
            closeFace();
          }
          return null;
        });
    }
    return visionRef.current;
  }, [closeFace]);

  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      stopStream(streamRef.current);
      closeFace();
      Object.values(shotsRef.current).forEach((s) => { if (s.preview) URL.revokeObjectURL(s.preview); });
    };
  }, [closeFace]);

  const openCamera = React.useCallback(async (want: "user" | "environment") => {
    unlockSound();
    sessionRef.current += 1;
    if (!cameraSupported()) { setStep({ name: "error", reason: "unsupported" }); return; }
    setStep({ name: "starting" });
    stopStream(streamRef.current);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: false, video: INK_CAMERA(want) });
      // Keep refocusing as the mark comes close (where the camera lets the page ask).
      const track = s.getVideoTracks()[0];
      const caps = (track?.getCapabilities?.() ?? {}) as { focusMode?: string[] };
      if (track && caps.focusMode?.includes("continuous")) {
        void track.applyConstraints({ advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet] }).catch(() => undefined);
      }
      streamRef.current = s;
      setStream(s);
      setFacing(want);
      setStep({ name: "stage" });
    } catch (e) {
      setStep({ name: "error", reason: cameraFailureOf(e) });
    }
  }, []);

  React.useEffect(() => {
    const v = videoRef.current;
    if (v && stream && v.srcObject !== stream) {
      v.srcObject = stream;
      void v.play().catch(() => undefined);
    }
  }, [stream, step.name]);

  /** The next view still to take, from `from` on, then from the start; null when none is left. */
  const nextOpen = React.useCallback((from: number, taken: Record<string, unknown>, skip: Record<string, boolean>) => {
    for (let n = 0; n < items.length; n++) {
      const i = (from + n) % items.length;
      if (!taken[items[i].key] && !skip[items[i].key]) return i;
    }
    return -1;
  }, [items]);

  const advance = React.useCallback((taken: Record<string, unknown>, skip: Record<string, boolean>) => {
    const next = nextOpen(idx + 1, taken, skip);
    if (next < 0) { stopCamera(); closeFace(); setStep({ name: "review" }); return; }
    setIdx(next);
  }, [idx, nextOpen, stopCamera, closeFace]);

  /**
   * Take this view's picture; true when one was kept. `sample` is the look it was judged on (the last look for "Take
   * it now"): its thumbnail is what close_b is checked against, so a picture taken by hand keeps that check too; the
   * mark's signature and box only when the judge saw the mark in it. `key`: the view the caller judged (the look's own;
   * a look still running for the view just taken never takes the next one). A view already taken is never taken again
   * here (until React shows the next view, a look or a second tap still reaches this one; a retake clears it first).
   */
  const take = React.useCallback(async (sample: InkSample | null, by: InkTakenBy, key?: string): Promise<boolean> => {
    const v = videoRef.current;
    if (!v || !item || takingRef.current || shotsRef.current[item.key] || (key !== undefined && key !== item.key)) return false;
    takingRef.current = true;
    try {
      const verdict = verdictRef.current;
      const lock: InkLock = verdict?.lock ?? "searching";
      const locked = onMark(verdict);
      const kind = item.mark.kind as MarkKind;
      const recheck: Recheck | null = by !== "manual" && kind !== "scar"
        ? { kind, face: placeOf(item.mark.place).region !== "body", stage: judgeRef.current?.stage(performance.now()) ?? 0 }
        : null;
      const pic = await takePicture(v, streamRef.current, recheck);
      if (!pic) return false;
      tel.track("ink_take", { bucket: item.view, data: { place: item.mark.place, kind, view: item.view, by, locked, lock, w: pic.width, h: pic.height } });
      const found = locked && sample?.mark.found ? sample.mark : null;
      refsRef.current[item.key] = {
        thumb: sample?.thumb ?? null, sig: found?.sig ?? null, box: found?.box ?? null, lock, by, session: sessionRef.current, at: performance.now(),
      };
      chime("bucket");
      buzz();
      setFlash(true);
      setTimeout(() => setFlash(false), 450);
      const key = item.key;
      const old = shotsRef.current[key];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      const box: InkShot["box"] = found?.box ? [found.box.x, found.box.y, found.box.w, found.box.h] : null;
      const next = { ...shotsRef.current, [key]: { mark: item.mark.id, view: item.view, ...pic, locked, by, box, preview: null } };
      shotsRef.current = next;
      setShots(next);
      setCountdown(null);
      advance(next, skipped);
      // The review shows a small copy, made off the critical path; the full photo is never put on screen.
      void reviewCopy(pic.blob).then((url) => {
        const cur = shotsRef.current[key];
        const preview = url ?? (cur?.blob === pic.blob && aliveRef.current ? URL.createObjectURL(pic.blob) : null);
        if (!preview) return;
        if (!aliveRef.current || !cur || cur.blob !== pic.blob) { URL.revokeObjectURL(preview); return; }
        const withPreview = { ...shotsRef.current, [key]: { ...cur, preview } };
        shotsRef.current = withPreview;
        setShots(withPreview);
      });
      return true;
    } finally {
      takingRef.current = false;
    }
  }, [item, advance, skipped, tel]);
  const takeRef = React.useRef(take);
  takeRef.current = take;

  // THE LOOK: a small colour copy of the frame, several times a second. The finder says where the mark is, the
  // judge whether it is locked on and good to keep; the picture is taken when it is ready.
  React.useEffect(() => {
    if (step.name !== "stage" || !item) return undefined;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const { region, side } = placeOf(item.mark.place);
    const kind = item.mark.kind as MarkKind;
    const first = item.view === "close_b" ? refsRef.current[`${item.mark.id}:close_a`] ?? null : null;
    // close_a taken by itself on a lock, a moment ago in this camera session: close_b starts locked on the same mark (it
    // is the same spot, a little to the side). Never from the review or after a camera switch: that box is old.
    const carry = first && first.lock === "locked" && first.by !== "manual" && first.box
      && first.session === sessionRef.current && performance.now() - first.at <= CARRY_MS ? { box: first.box } : null;
    const judge = new InkJudge(performance.now(), {
      view: item.view, kind, region, side, ref: first ? { thumb: first.thumb, sig: first.sig } : null, carry,
    });
    judgeRef.current = judge;
    lastSampleRef.current = null;
    verdictRef.current = null;
    lastFoundRef.current = null;
    let shown = "";
    let looks = 0;
    const work: number[] = [];
    const viewStart = performance.now();
    let reportAt = viewStart;
    let reports = 0;
    tel.track("ink_view", { bucket: item.view, data: { place: item.mark.place, kind, view: item.view, region, side, facing: facingRef.current, carry: !!carry } });
    let lastFind: InkFind | null = null;
    let prev: Box01 | null = carry?.box ?? null;
    let gained = !!carry;
    let focusAt: { x: number; y: number } | null = null;
    let focusT = -Infinity;
    // A place whose side the face model checks: when it last read the side of the mark in a look. While it sees a
    // face, nothing is taken by itself until it has read the side of the mark the lock is on now (gained, or jumped
    // to another): on a slow phone the face model runs more than 1.2 s apart (4 x its own time), and a lock that slid
    // to the other cheek was held still long enough to be taken in between, its side unknown (2026-10-07 review).
    // No face seen (or no face model): pixels alone, no wait. Nor without a lock (a scar taken on skin alone has no
    // mark to read the side of).
    const sideHelp = sideChecked(region, side);
    let sideAt = -Infinity;
    // The late countdown, on the look's clock: when it started, and the number on screen.
    let countFrom: number | null = null;
    let counted: number | null = null;
    const stopCounting = () => {
      countFrom = null;
      if (counted !== null) {
        counted = null;
        setCountdown(null);
      }
    };
    // This view's picture is being taken: its look stops here. Until React swaps this effect for the next view's, the
    // interval still ticks with a judge that is still ready; it took the same view twice, or the next one at once
    // with this view's frame (2026-10-07 review). Back on when the picture could not be taken (or taking it failed:
    // the look must not stay stopped with nothing kept).
    let done = false;
    const takeNow = (s: InkSample | null, by: InkTakenBy) => {
      done = true;
      void takeRef.current(s, by, item.key).then((kept) => { if (!kept) done = false; }, () => { done = false; });
    };
    setLook(lookStart(region));
    setCountdown(null);
    const id = setInterval(() => {
      const v = videoRef.current;
      if (done || shotsRef.current[item.key] || !v || !ctx || v.readyState < 2 || !v.videoWidth || takingRef.current) return;
      const t0 = performance.now();
      const fw = v.videoWidth;
      const fh = v.videoHeight;
      if (frameRef.current?.w !== fw || frameRef.current?.h !== fh) {
        frameRef.current = { w: fw, h: fh };
        setFrame(frameRef.current);
      }
      const k = LOOK_SIDE / Math.max(fw, fh);
      const w = Math.max(16, Math.round(fw * k));
      const h = Math.max(16, Math.round(fh * k));
      if (canvas.width !== w || canvas.height !== h) {
        // Sized only when the size changes: a resize clears the context, and with it the area-averaged downscale the
        // finder needs (a plain bilinear one shows arm hair as thick strokes).
        canvas.width = w;
        canvas.height = h;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        lookSizeRef.current = { w, h };
        lastFind = null;
      }
      ctx.drawImage(v, 0, 0, w, h);
      const rgba = ctx.getImageData(0, 0, w, h).data;
      const t = performance.now();
      const m = measureInk(toGray(rgba, w, h), t);
      const stage = judge.stage(t);
      const tol = LOCKJ.centre[region === "body" ? "body" : "face"][stage];
      // The face fix only while it still describes this look (until the next is due, the picture unmoved, the same size).
      const raw = fixRef.current;
      const fix = raw && raw.w === w && raw.h === h ? freshFix(raw, t, m.thumb, faceAgeRef.current) : null;
      const mean = work.length ? work.reduce((a, b) => a + b, 0) / work.length : 0;
      const every = mean > LOOK_SLOW_MS[1] ? 3 : mean > LOOK_SLOW_MS[0] ? 2 : 1;
      const fresh = !lastFind || looks % every === 0;
      looks += 1;
      const mark = fresh
        ? finderFor(w, h)(rgba, kind, {
          face: region !== "body", stage, prev, hint: fix ? anchorBox(fix) : null, mask: fix?.mask ?? null, guide: guideOf(tol),
        })
        : lastFind;
      lastFind = mark;
      if (fresh) {
        work.push(performance.now() - t0);
        if (work.length > LOOK_MEAN) work.shift();
        // What a look costs on this device (the e2e runs report it): written straight on the element, no render.
        if (work.length === LOOK_MEAN && looks % LOOK_MEAN === 0) stageRef.current?.setAttribute("data-look-ms", mean.toFixed(1));
      }
      const at = fix && mark.found && mark.centre ? mark.centre : null;
      const s: InkSample = { ...m, mark, fresh, side: at ? sideOf(fix, at) : null, belowChin: at ? belowChin(fix, at) : null };
      lastSampleRef.current = s;
      if (mark.found) lastFoundRef.current = mark;
      if (s.side !== null) sideAt = t;
      const verdict = judge.push(s);
      verdictRef.current = verdict;
      if (t - reportAt >= INK_REPORT_MS && reports < INK_REPORTS_MAX) {
        reportAt = t;
        reports += 1;
        tel.track("ink_look", { bucket: item.view, data: {
          place: item.mark.place, kind, view: item.view, s: r1((t - viewStart) / 1000), fw, fh, w, h, stage,
          mean: r3(m.mean), glare: r3(m.glare), sharp: r1(m.sharp), found: mark.found, why: mark.why ?? "",
          skin: r3(mark.skin), skinBox: r3(mark.skinBox), skinY: r3(mark.skinY), hairy: mark.hairy,
          span: r3(mark.span), share: r3(mark.share), contrast: r3(mark.contrast), solidity: r3(mark.solidity),
          lock: verdict.lock, problem: verdict.problem ?? "", stuck: verdict.stuck, faceFix: !!fix, lookMs: r1(mean), every,
        } });
      }
      const sideWait = sideHelp && verdict.lock !== "searching" && fixRef.current !== null && sideAt < t - verdict.lockHeldMs;
      // The finder keeps to the mark it found last while the lock holds (the box does not hop between marks).
      prev = mark.found ? mark.box : verdict.lock !== "searching" ? prev : null;
      const now: LookState = { lock: verdict.lock, box: verdict.box, tol: verdict.tol, problem: verdict.problem, stuck: verdict.stuck, skinOnly: verdict.skinOnly };
      const key = lookKey(now);
      if (key !== shown) {
        shown = key;
        setLook(now);
      }
      if (verdict.lock !== "searching") {
        if (!gained) {
          gained = true;
          buzz(15);
        }
        const c = lastFoundRef.current?.centre;
        if (c && (!focusAt || (Math.hypot(c.x - focusAt.x, c.y - focusAt.y) > FOCUS_MOVE && t - focusT >= FOCUS_EVERY_MS))) {
          focusAt = c;
          focusT = t;
          focusOn(streamRef.current, c);
        }
      } else focusAt = null;
      if (verdict.ready && !sideWait) {
        stopCounting();
        takeNow(s, "auto");
        return;
      }
      // The late countdown, 3-2-1 on the look's clock, only while the judge allows it (a lock held on one mark,
      // nothing but framing or the wait left), cancelled on the first look it does not; at 0 it takes the first look
      // that shows the mark (an empty frame never counts down, and a miss the lock is held through is not taken).
      if (!verdict.countdown) stopCounting();
      else {
        if (countFrom === null) countFrom = t;
        const n = 3 - Math.floor((t - countFrom) / 1000);
        if (n > 0) {
          if (n !== counted) {
            counted = n;
            setCountdown(n);
          }
        } else if (verdict.seen && !sideWait) {
          stopCounting();
          takeNow(s, "countdown");
        }
      }
    }, LOOK_MS);
    return () => {
      clearInterval(id);
      if (judgeRef.current === judge) judgeRef.current = null;
    };
  }, [step.name, item, tel]);

  // THE FACE HELP (face, neck and head marks): the face model now and then, never inside the look. Its fix masks the
  // eyes, brows and lips for the finder, points at the named place and checks the side. It never blocks the scan.
  React.useEffect(() => {
    if (step.name !== "stage" || !item || !faceAssist) return undefined;
    const place = item.mark.place;
    const { region, side } = placeOf(place);
    if (region === "body" || !(sideChecked(region, side) || INK_FACE_PLACES[place]?.anchor)) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    fixRef.current = null;
    faceAgeRef.current = FACE_FIX.maxAgeMs;
    const later = (ms: number) => {
      if (!stopped) timer = setTimeout(() => void tick(), ms);
    };
    const tick = async () => {
      if (stopped || faceOffRef.current) return;
      const vision = await faceVision();
      if (stopped || !vision) return;
      const v = videoRef.current;
      const s = lastSampleRef.current;
      const size = lookSizeRef.current;
      // Not while a picture is taken.
      if (takingRef.current || !v || v.readyState < 2 || !s || !size) {
        later(FACE_FIRST_MS);
        return;
      }
      const t0 = performance.now();
      let lm: Array<{ x: number; y: number }> | null = null;
      try {
        lm = vision.detect(v, t0, false).face?.faceLandmarks?.[0] ?? null;
      } catch {
        // The face model failed: off for the rest of the scan, without a word (pixels alone carry on).
        faceOffRef.current = true;
        closeFace();
        return;
      }
      if (stopped) return;
      const ms = performance.now() - t0;
      fixRef.current = faceFixOf(lm, place, size, s.thumb, t0);
      const every = Math.max(FACE_EVERY_MS, 4 * ms);
      faceAgeRef.current = Math.max(FACE_FIX.maxAgeMs, FACE_LAST * every);
      later(every);
    };
    later(FACE_FIRST_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      fixRef.current = null;
    };
  }, [step.name, item, faceAssist, faceVision, closeFace]);

  const skipWide = () => {
    if (!item || item.view !== "wide") return;
    const skip = { ...skipped, [item.key]: true };
    setSkipped(skip);
    advance(shots, skip);
  };

  const retake = (key: string) => {
    const i = items.findIndex((x) => x.key === key);
    if (i < 0) return;
    const { [key]: gone, ...rest } = shots;
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    shotsRef.current = rest;
    setShots(rest);
    setSkipped((s) => ({ ...s, [key]: false }));
    setIdx(i);
    void openCamera(facing);
  };

  const done = items.filter((x) => shots[x.key]).length;
  const closeUpsDone = marks.every((m) => shots[`${m.id}:close_a`] && shots[`${m.id}:close_b`]);
  const place = (m: InkMark) => t(`faceLock.marks.place.${m.place}`);
  const kind = (m: InkMark) => t(`faceLock.marks.kind.${m.kind}`);
  const noun = (m: InkMark) => t(`faceLock.ink.noun.${m.kind}`);
  const close = () => { stopCamera(); closeFace(); onClose(); };

  /** What to change, in plain words ("moving" stays unsaid: the person is told when it is taken). */
  const fixLine = (s: LookState, m: InkMark): string => {
    if (s.stuck) return t("faceLock.ink.fix.stuck", { noun: noun(m) });
    if (s.skinOnly && (!s.problem || s.problem === "moving" || s.problem === "blur")) return t("faceLock.ink.fix.scar");
    if (!s.problem || s.problem === "moving") return "";
    if (s.problem === "wrongside") {
      const side = placeOf(m.place).side;
      return t(side === "left" ? "faceLock.ink.fix.wrongLeft" : side === "right" ? "faceLock.ink.fix.wrongRight" : "faceLock.ink.fix.wrongCenter");
    }
    if (s.problem === "same") return t(`faceLock.ink.fix.same_${sideWayOf(m.place)}`, { noun: noun(m) });
    return t(`faceLock.ink.fix.${s.problem}`, { noun: noun(m) });
  };

  let body: React.ReactNode = null;
  if (step.name === "intro") {
    body = (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="ink-intro">
        <header className="flex items-start justify-between gap-3 px-4 pt-3">
          <h2 className="text-[26px] font-semibold">{t("faceLock.ink.title")}</h2>
          <button type="button" className={SCAN_UI.iconButton} onClick={close} aria-label={t("faceLock.scan.close")} data-testid="ink-close"><X aria-hidden /></button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <p className="mb-3">{t("faceLock.ink.lead")}</p>
          <ul className="grid gap-2">
            {marks.map((m) => (
              <li key={m.id} className={`rounded-xl border p-3 ${SCAN_UI.panel}`}>
                <b className="block">{kind(m)} · {place(m)}</b>
                <span className={SCAN_UI.mutedColor}>{t("faceLock.ink.pictures", { count: inkViews(m.place).length })}</span>
              </li>
            ))}
          </ul>
          <div className={`mt-3 rounded-xl border p-3 ${SCAN_UI.panel}`}>
            <b className="block">{t("faceLock.scan.intro.tipsTitle")}</b>
            <span className={SCAN_UI.mutedColor}>{t("faceLock.ink.tips")}</span>
          </div>
        </div>
        <div className="grid gap-2 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-2">
          <button type="button" className={SCAN_UI.primaryButton} onClick={() => void openCamera(facing)} data-testid="ink-start">
            <Camera aria-hidden /> {t("faceLock.ink.start")}
          </button>
          <p className={`text-center text-[18px] ${SCAN_UI.mutedColor}`}>{t("faceLock.scan.intro.cameraNote")}</p>
        </div>
      </div>
    );
  } else if (step.name === "starting") {
    body = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" data-testid="ink-starting">
        <Loader2 className="h-10 w-10 motion-safe:animate-spin" aria-hidden />
        <p aria-live="polite">{t("faceLock.scan.camera.starting")}</p>
      </div>
    );
  } else if (step.name === "error") {
    body = (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="ink-camera-error" data-reason={step.reason}>
        <header className="flex justify-end px-3 pt-2">
          <button type="button" className={SCAN_UI.iconButton} onClick={close} aria-label={t("faceLock.scan.close")}><X aria-hidden /></button>
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <h2 className="text-[24px] font-semibold">{t(`faceLock.scan.camera.${step.reason}Title`)}</h2>
          <p className={SCAN_UI.muted}>{t(`faceLock.scan.camera.${step.reason}`)}</p>
        </div>
        <div className="grid gap-2 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-2">
          <button type="button" className={SCAN_UI.primaryButton} onClick={() => void openCamera(facing)} data-testid="ink-retry">{t("faceLock.scan.camera.retry")}</button>
        </div>
      </div>
    );
  } else if (step.name === "stage" && item) {
    const helper = needsHelper(item.mark.place);
    body = (
      <div ref={stageRef} className="relative flex min-h-0 flex-1 flex-col" data-testid="ink-stage" data-view={item.view} data-mark={item.mark.id}
        data-lock={look.lock} data-lock-box={look.box ? boxAttr(look.box) : undefined} data-problem={look.problem ?? ""} data-stuck={look.stuck ? "1" : "0"}>
        <header className="z-10 flex items-center justify-between gap-2 px-3 pt-2">
          <span className={`text-[16px] ${SCAN_UI.mutedColor}`} data-testid="ink-progress">{t("faceLock.ink.progress", { n: done + 1, total: items.length })}</span>
          <div className="flex gap-1">
            <button type="button" className={SCAN_UI.iconButton} onClick={() => void openCamera(facing === "user" ? "environment" : "user")}
              aria-label={t("faceLock.ink.switchCamera")} title={t("faceLock.ink.switchCamera")} data-testid="ink-switch"><SwitchCamera aria-hidden /></button>
            <button type="button" className={SCAN_UI.iconButton} onClick={close} aria-label={t("faceLock.scan.close")} data-testid="ink-close"><X aria-hidden /></button>
          </div>
        </header>
        <div className="relative mx-3 mt-2 min-h-0 flex-1 overflow-hidden rounded-2xl bg-black" data-testid="ink-preview">
          <video ref={videoRef} playsInline muted autoPlay data-testid="ink-video"
            className="h-full w-full object-cover" style={facing === "user" ? { transform: "scaleX(-1)" } : undefined} />
          {/* Where the mark goes (exactly what the judge counts as centred) and what it locked on to. */}
          {frame && <InkOverlay width={frame.w} height={frame.h} mirrored={facing === "user"} lock={look.lock} box={look.box} tol={look.tol} />}
          {flash && <div aria-hidden className="pointer-events-none absolute inset-0 bg-white/60" data-testid="ink-flash" />}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center text-[96px] font-bold text-white drop-shadow" data-testid="ink-countdown" aria-live="assertive">{countdown}</div>
          )}
        </div>
        <div className="grid gap-2 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
          <p className="text-[16px] font-semibold uppercase tracking-wide text-[#b4bac4]">{kind(item.mark)} · {place(item.mark)}</p>
          <p className="text-[22px] font-semibold leading-snug" data-testid="ink-instruction">
            {t(item.view === "close_b" ? `faceLock.ink.view.close_b_${sideWayOf(item.mark.place)}` : `faceLock.ink.view.${item.view}`, { noun: noun(item.mark) })}
          </p>
          <p className="min-h-[1.5em] text-[18px] text-[#f5c451]" aria-live="polite" data-testid="ink-problem">
            {countdown !== null ? t("faceLock.ink.countdown", { n: countdown }) : fixLine(look, item.mark)}
          </p>
          {helper && <p className={`text-[16px] ${SCAN_UI.mutedColor}`}>{t("faceLock.ink.helper")}</p>}
          {/* Always works, so the scan always finishes; a picture with no lock is flagged in the review. Pulses once the view is stuck. */}
          <button type="button" className={`${SCAN_UI.primaryButton}${look.stuck ? " motion-safe:animate-pulse ring-2 ring-[#f5c451]" : ""}`}
            onClick={() => void take(lastSampleRef.current, "manual")} data-testid="ink-shutter" data-stuck={look.stuck ? "1" : undefined}>
            <Camera aria-hidden /> {t("faceLock.ink.shutter")}
          </button>
          {item.view === "wide" && (
            <button type="button" className={SCAN_UI.ghostButton} onClick={skipWide} data-testid="ink-skip-wide">{t("faceLock.ink.skipWide")}</button>
          )}
        </div>
      </div>
    );
  } else if (step.name === "review") {
    body = (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="ink-review">
        <header className="flex items-start justify-between gap-3 px-4 pt-3">
          <h2 className="text-[24px] font-semibold">{t("faceLock.ink.reviewTitle")}</h2>
          <button type="button" className={SCAN_UI.iconButton} onClick={close} aria-label={t("faceLock.scan.close")}><X aria-hidden /></button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <p className={`mb-3 ${SCAN_UI.mutedColor}`}>{t("faceLock.ink.reviewLead")}</p>
          {marks.map((m) => (
            <section key={m.id} className="mb-4">
              <h3 className="mb-2 font-semibold">{kind(m)} · {place(m)}</h3>
              <div className="grid grid-cols-3 gap-2">
                {inkViews(m.place).map((view) => {
                  const key = `${m.id}:${view}`;
                  const s = shots[key];
                  // Taken by hand with no lock: maybe the mark is not in it (a scar may be too pale to lock on: never flagged).
                  const unsure = !!s && !s.locked && m.kind !== "scar";
                  return (
                    <div key={key} className={`grid gap-1 rounded-lg border p-1.5 ${SCAN_UI.panel}`} data-testid={`ink-tile-${key}`}
                      data-by={s?.by} data-locked={s ? (s.locked ? "1" : "0") : undefined}>
                      <div className="aspect-[3/4] overflow-hidden rounded-md bg-black">
                        {s?.preview ? <img src={s.preview} alt={t(`faceLock.ink.viewName.${view}`)} className="h-full w-full object-cover" /> : null}
                      </div>
                      <span className="text-[14px]">{t(`faceLock.ink.viewName.${view}`)}{!s && skipped[key] ? ` · ${t("faceLock.ink.skipped")}` : ""}</span>
                      {unsure && <span className="text-[13px] leading-snug text-[#f5c451]" data-testid={`ink-nolock-${key}`}>{t("faceLock.ink.noLock", { noun: noun(m) })}</span>}
                      <button type="button" className="inline-flex items-center gap-1 text-[14px] underline" onClick={() => retake(key)} data-testid={`ink-retake-${key}`}>
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden /> {s ? t("faceLock.scan.review.retake") : t("faceLock.ink.take")}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <div className="grid gap-2 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-2">
          <button type="button" className={SCAN_UI.primaryButton} disabled={!closeUpsDone} data-testid="ink-use"
            onClick={() => onDone(items.filter((x) => shots[x.key]).map((x) => {
              const { preview: _preview, ...shot } = shots[x.key];
              return shot;
            }))}>
            <Check aria-hidden /> {t("faceLock.ink.use", { count: done })}
          </button>
          {!closeUpsDone && <p className={`text-center text-[16px] ${SCAN_UI.mutedColor}`}>{t("faceLock.ink.needCloseUps")}</p>}
        </div>
      </div>
    );
  }

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[420] bg-black" />
        <DialogPrimitive.Content
          className={`fixed inset-0 z-[421] flex flex-col outline-none ${SCAN_UI.surface}`}
          data-testid="ink-scan" data-step={step.name}
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">{t("faceLock.ink.title")}</DialogPrimitive.Title>
          {body}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
